import React from 'react';
import { Link2, Link2Off, X, Plus, Info, AlertTriangle, FileText, Table2 } from 'lucide-react';
import { FACET_LABELS, toggleKey, toggleFacetColumn } from './columnFacets';
import { describeSchedule, formatTime, nextRuns } from './nextRuns';

/* ------------------------------------------------------------------ chips */

export function Chip({ on, dim, onClick, children, title }) {
  return (
    <button
      type="button"
      className={`rn-sch-chip${on ? ' rn-on' : ''}${dim ? ' rn-dim' : ''}`}
      onClick={onClick}
      title={title}
      aria-pressed={on}
    >
      {children}
    </button>
  );
}

function Note({ tone = 'info', children }) {
  const Icon = tone === 'warn' ? AlertTriangle : Info;
  return (
    <div className={`rn-sch-note${tone === 'warn' ? ' rn-warn' : ''}`}>
      <Icon />
      <div>{children}</div>
    </div>
  );
}

function Block({ title, sub, children }) {
  return (
    <section className="rn-sch-block">
      <header>
        <h3>{title}</h3>
        {sub && <p>{sub}</p>}
      </header>
      {children}
    </section>
  );
}

/* -------------------------------------------------------------- timeframe */

export function TimeframeBlock({ periods, period, snapshotPeriod, onPeriod, onSnapshotPeriod, currentViewLabel }) {
  const linked = !snapshotPeriod;
  const effectiveSnapshot = snapshotPeriod || period;
  return (
    <Block title="Timeframe" sub="What each part of the email covers. They can differ.">
      <label className="rn-sch-label" htmlFor="rn-sch-attachment-period">Attachment covers</label>
      <div className="rn-sch-chips" id="rn-sch-attachment-period">
        {periods.map((item) => (
          <Chip key={item.value} on={period === item.value} onClick={() => onPeriod(item.value)}>
            {item.label}
          </Chip>
        ))}
      </div>
      {period === 'currentView' && currentViewLabel && (
        <p className="rn-sch-hint">Fixed range: {currentViewLabel}</p>
      )}

      <div className="rn-sch-linkrow">
        {linked ? <Link2 /> : <Link2Off className="rn-sch-unlinked" />}
        <span>
          {linked
            ? 'The email body covers the same period as the attachment.'
            : 'The email body is unlinked from the attachment.'}
        </span>
        <button
          type="button"
          className="rn-sch-chip rn-sch-chip-sm"
          onClick={() => onSnapshotPeriod(linked ? (period === 'currentView' ? 'yesterday' : period) : null)}
        >
          {linked ? 'Unlink' : 'Re-link'}
        </button>
      </div>

      {!linked && (
        <>
          <label className="rn-sch-label" htmlFor="rn-sch-snapshot-period">
            Snapshot covers <span>— the table in the email body</span>
          </label>
          <div className="rn-sch-chips" id="rn-sch-snapshot-period">
            {periods.filter((item) => item.value !== 'currentView').map((item) => (
              <Chip key={item.value} on={effectiveSnapshot === item.value} onClick={() => onSnapshotPeriod(item.value)}>
                {item.label}
              </Chip>
            ))}
          </div>
          <Note>
            Two periods means two queries each time this runs, instead of one.
          </Note>
        </>
      )}
    </Block>
  );
}

/* ----------------------------------------------------------------- detail */

function ColumnGrid({ model, selected, onChange }) {
  const order = model.columns.map((c) => c.key);
  return (
    <table className="rn-sch-matrix">
      <thead>
        <tr>
          <th scope="col">Metric</th>
          {model.facets.map((facet) => (
            <th scope="col" key={facet}>
              <button
                type="button"
                className="rn-sch-facet-toggle"
                onClick={() => onChange(toggleFacetColumn(selected, model, facet, order))}
                title={`Toggle every ${FACET_LABELS[facet].toLowerCase()} column`}
              >
                {FACET_LABELS[facet]}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {model.metrics.map((metric) => (
          <tr key={metric.key}>
            <th scope="row" title={metric.description || undefined}>{metric.label}</th>
            {model.facets.map((facet) => {
              const key = metric.cells[facet];
              return (
                <td key={facet}>
                  {key ? (
                    <input
                      type="checkbox"
                      checked={selected.includes(key)}
                      onChange={() => onChange(toggleKey(selected, key, order))}
                      aria-label={`${metric.label} ${FACET_LABELS[facet].toLowerCase()}`}
                    />
                  ) : (
                    <span className="rn-sch-na" aria-hidden="true">·</span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ColumnList({ model, selected, onChange }) {
  const order = model.columns.map((c) => c.key);
  return (
    <div className="rn-sch-collist">
      {model.columns.map((column) => (
        <label key={column.key} title={column.description || undefined}>
          <input
            type="checkbox"
            checked={selected.includes(column.key)}
            onChange={() => onChange(toggleKey(selected, column.key, order))}
          />
          <span>{column.label}</span>
        </label>
      ))}
    </div>
  );
}

export function DetailBlock({
  model, selectedColumns, onColumns, tiers, expandLevel, onExpandLevel, rowEstimate, rowCap, expandDisabledReason,
}) {
  const total = model.mode === 'grid'
    ? model.columns.length
    : model.columns.length;
  const level = Number.isInteger(expandLevel) ? expandLevel : (tiers ? tiers.length - 1 : 0);
  const overCap = rowEstimate != null && rowCap != null && rowEstimate > rowCap;

  return (
    <Block title="Detail" sub="Which columns print, and how far the rows open.">
      <label className="rn-sch-label">
        Columns <span>— {selectedColumns.length || total} of {total} selected</span>
      </label>
      {model.mode === 'grid'
        ? <ColumnGrid model={model} selected={selectedColumns} onChange={onColumns} />
        : <ColumnList model={model} selected={selectedColumns} onChange={onColumns} />}

      {tiers && tiers.length > 1 && (
        <div className="rn-sch-expand">
          <label className={`rn-sch-label${expandDisabledReason ? ' rn-dim' : ''}`}>Expand rows to</label>
          <div className="rn-sch-step" role="group" aria-label="Expand rows to">
            {tiers.map((tier, index) => (
              <React.Fragment key={tier}>
                {index > 0 && <span className="rn-sch-rail" aria-hidden="true" />}
                <button
                  type="button"
                  className={`rn-sch-lvl${!expandDisabledReason && index <= level ? ' rn-on' : ''}${expandDisabledReason ? ' rn-off' : ''}`}
                  onClick={() => onExpandLevel(index)}
                  disabled={Boolean(expandDisabledReason)}
                  aria-pressed={!expandDisabledReason && index <= level}
                >
                  {index === 0 ? tier : `+ ${tier}`}
                </button>
              </React.Fragment>
            ))}
          </div>
          {expandDisabledReason
            ? <Note>{expandDisabledReason}</Note>
            : overCap
              ? (
                <Note tone="warn">
                  <b>≈ {rowEstimate.toLocaleString()} rows</b> — over the {rowCap.toLocaleString()}-row print
                  cap. The deepest level will be dropped from the PDF and the book will say so.
                </Note>
              )
              : rowEstimate != null && (
                <p className="rn-sch-hint">≈ {rowEstimate.toLocaleString()} rows · prints in full</p>
              )}
        </div>
      )}
    </Block>
  );
}

/* ------------------------------------------------------------- attachment */

export function AttachmentBlock({ format, onFormat, pageCount }) {
  const option = (value, Icon, title, description) => (
    <label className={`rn-sch-opt${format === value ? ' rn-on' : ''}`}>
      <input type="radio" name="rn-sch-format" checked={format === value} onChange={() => onFormat(value)} />
      <span className="rn-sch-opt-icon"><Icon /></span>
      <span className="rn-sch-opt-body">
        <b>{title}</b>
        <small>{description}</small>
      </span>
    </label>
  );
  return (
    <Block title="Attachment" sub="What rides along with the email.">
      {option('pdf', FileText, 'PDF · the designed book',
        `One sheet per report page, full matrix grain, KPI strip and charts${pageCount ? ` · ${pageCount} page${pageCount === 1 ? '' : 's'}` : ''}.`)}
      {option('xlsx', Table2, 'Excel · a workable grid',
        'One sheet per report page at your expansion level: a column per tier (property, department, position…) so it filters and pivots, your selected columns across, totals row. Real numeric cells.')}
    </Block>
  );
}

/* --------------------------------------------------------------- schedule */

const CADENCES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];
const WEEK_DAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
];
export const MAX_TIMES = 4;

export function ScheduleBlock({ trigger, onTrigger, timezones, closeDay = 15 }) {
  const set = (patch) => onTrigger({ ...trigger, ...patch });
  const times = trigger.times || [];
  const days = trigger.days || [];

  const setCadence = (frequency) => set({
    frequency,
    anchor: undefined,
    // daily means every day — scheduleUtils only fires on listed days, so a
    // daily schedule with a partial list would silently become weekly
    days: frequency === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : frequency === 'weekly' ? [1] : [1],
  });
  const toggleDay = (value) => {
    // With an anchor on, the day list is hidden and reads as unselected, so
    // a click picks that day outright instead of toggling the list beneath.
    if (trigger.anchor) {
      set({ days: [value], anchor: undefined });
      return;
    }
    const next = days.includes(value) ? days.filter((d) => d !== value) : [...days, value];
    if (!next.length) return;
    set({ days: next.sort((a, b) => a - b), anchor: undefined });
  };
  const addTime = () => {
    if (times.length >= MAX_TIMES) return;
    // offer a spread across the day, skipping any slot already taken — two
    // identical times would fire the same email twice
    const candidate = ['12:00', '17:00', '06:00', '21:00'].find((slot) => !times.includes(slot));
    if (!candidate) return;
    set({ times: [...times, candidate].sort() });
  };

  const runs = nextRuns(trigger, { count: 3, closeDay });

  return (
    <Block title="Schedule" sub="When it fires. Multiple times a day, or only on the days that matter.">
      <label className="rn-sch-label">Cadence</label>
      <div className="rn-sch-chips">
        {CADENCES.map((item) => (
          <Chip key={item.value} on={trigger.frequency === item.value} onClick={() => setCadence(item.value)}>
            {item.label}
          </Chip>
        ))}
      </div>

      {trigger.frequency === 'weekly' && (
        <>
          <label className="rn-sch-label">Days of week</label>
          <div className="rn-sch-chips">
            {WEEK_DAYS.map((day) => (
              <Chip key={day.value} on={days.includes(day.value)} onClick={() => toggleDay(day.value)}>
                {day.label}
              </Chip>
            ))}
          </div>
        </>
      )}

      {trigger.frequency === 'monthly' && (
        <>
          <label className="rn-sch-label">Days of month</label>
          <div className="rn-sch-daygrid">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                type="button"
                className={`rn-sch-day${!trigger.anchor && days.includes(day) ? ' rn-on' : ''}`}
                onClick={() => toggleDay(day)}
                aria-pressed={!trigger.anchor && days.includes(day)}
              >
                {day}
              </button>
            ))}
          </div>
          <div className="rn-sch-chips rn-sch-anchors">
            <Chip on={trigger.anchor === 'lastBusinessDay'} onClick={() => set({ anchor: trigger.anchor === 'lastBusinessDay' ? undefined : 'lastBusinessDay' })}>
              Last business day
            </Chip>
            <Chip
              on={trigger.anchor === 'afterClose'}
              onClick={() => set({ anchor: trigger.anchor === 'afterClose' ? undefined : 'afterClose' })}
              title={`Your books close on day ${closeDay}`}
            >
              Day after close
            </Chip>
          </div>
        </>
      )}

      <label className="rn-sch-label">
        Send at <span>— up to {MAX_TIMES} times a day</span>
      </label>
      <div className="rn-sch-chips">
        {times.map((time, index) => (
          <span className="rn-sch-tpill" key={`${time}-${index}`}>
            <input
              type="time"
              value={time}
              onChange={(event) => {
                const next = [...times];
                next[index] = event.target.value;
                set({ times: next });
              }}
              aria-label={`Send time ${index + 1}`}
            />
            {times.length > 1 && (
              <button
                type="button"
                className="rn-sch-x"
                onClick={() => set({ times: times.filter((_, i) => i !== index) })}
                aria-label={`Remove ${formatTime(time)}`}
              >
                <X />
              </button>
            )}
          </span>
        ))}
        {times.length < MAX_TIMES && (
          <button type="button" className="rn-sch-chip" onClick={addTime}><Plus /> add time</button>
        )}
      </div>

      <label className="rn-sch-label" htmlFor="rn-sch-tz">Timezone</label>
      <select
        id="rn-sch-tz"
        className="rn-sch-select"
        value={trigger.timezone}
        onChange={(event) => set({ timezone: event.target.value })}
      >
        {timezones.map((tz) => <option key={tz.value || tz} value={tz.value || tz}>{tz.label || tz}</option>)}
      </select>

      <div className="rn-sch-readback">
        <p>{describeSchedule(trigger)}</p>
        {runs.length > 0 && (
          <p className="rn-sch-hint">
            Next {runs.length === 1 ? 'run' : `${runs.length === 2 ? 'two' : 'three'}`} ·{' '}
            {runs.map((run) => new Intl.DateTimeFormat('en-US', {
              timeZone: trigger.timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            }).format(run)).join(' · ')}
          </p>
        )}
      </div>
    </Block>
  );
}

/* ------------------------------------------------------------- what lands */

export function WhatLands({ subject, snapshotLabel, attachmentLabel, fileName, nextRun, timezone, recipientCount }) {
  return (
    <div className="rn-sch-lands">
      <span className="rn-sch-eyebrow">What lands</span>
      <p>Subject <b>{subject}</b></p>
      <p className="rn-sch-hint">
        Body: {snapshotLabel} · Attachment: <span className="rn-sch-mono">{fileName}</span> ({attachmentLabel})
      </p>
      <p className="rn-sch-hint">
        {nextRun
          ? <>Next send <span className="rn-sch-mono">{new Intl.DateTimeFormat('en-US', {
            timeZone: timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          }).format(nextRun)}</span></>
          : 'No next send — check the schedule'}
        {' · '}{recipientCount} recipient{recipientCount === 1 ? '' : 's'}
      </p>
    </div>
  );
}
